import { Link as RouterLink, useLocation } from "react-router-dom";
import { Avatar, Box, Stack, Switch, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import InputControl from "../../../shared/components/InputControl";
import { CountrySelect, CustomeDialog, LoadingButton } from "../../../shared";
import { useFtiForm } from "../hooks";

const FtiForm = ({
  customer,
  initialValues,
  remarks,
  submitApi,
  onSuccess,
}) => {
  const { state, validations, onChange, onSubmit } = useFtiForm({
    initialValues,
    customerId: customer?.id,
    submitApi,
    onSuccess,
  });
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  // A guest order has no customer profile to save the business details to
  const isGuest = customer && !customer.id;

  return (
    <Stack>
      {customer && (
        <Stack
          direction="row"
          alignItems="center"
          gap="8px"
          flexWrap="wrap"
          sx={{ mx: "-16px", px: "16px", py: "8px", bgcolor: "#F7F7F7" }}
        >
          <Avatar sx={{ width: 28, height: 28, bgcolor: "#E6E6E6" }}>
            <PersonOutlineIcon sx={{ fontSize: 18, color: "#666666" }} />
          </Avatar>
          {/* Guests have no customer record, so only registered customers link to their details,
              and store admins have no customers page. It opens in a new tab so the form being
              filled isn't lost. */}
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            flexWrap="wrap"
            {...(customer.id && !isStoreAdmin && {
              component: RouterLink,
              to: `/customers/details/${customer.id}`,
              target: "_blank",
              rel: "noopener noreferrer",
              sx: { color: "inherit", textDecoration: "none" },
            })}
          >
            <Typography variant="body2" fontWeight={600}>
              {customer.name || "--"}
            </Typography>
            {customer.email && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textDecoration: "underline" }}
              >
                {customer.email}
              </Typography>
            )}
            {customer.phone && (
              <Typography variant="body2" color="text.secondary">
                • {customer.phone}
              </Typography>
            )}
          </Stack>
        </Stack>
      )}

      <Stack gap="16px" pt="20px">
        <InputControl
          name="business_name"
          label="Customer Name/ชื่อลูกค้า"
          required
          value={state.formData.business_name}
          onChange={onChange}
          error={!!validations.business_name}
          helperText={validations.business_name}
        />
        <InputControl
          name="business_branch"
          label="Branch/สาขา"
          value={state.formData.business_branch}
          onChange={onChange}
        />
        <InputControl
          name="full_business_address"
          label="Customer Address/ที่อยู่ลูกค้า"
          required
          value={state.formData.full_business_address}
          onChange={onChange}
          error={!!validations.full_business_address}
          helperText={validations.full_business_address}
        />
        <Stack direction={{ xs: "column", sm: "row" }} gap="16px">
          <InputControl
            name="business_state"
            label="State/จังหวัด"
            required
            value={state.formData.business_state}
            onChange={onChange}
            error={!!validations.business_state}
            helperText={validations.business_state}
          />
          <InputControl
            name="business_postcode"
            label="Post Code/รหัสไปรษณีย์"
            required
            value={state.formData.business_postcode}
            onChange={onChange}
            error={!!validations.business_postcode}
            helperText={validations.business_postcode}
          />
        </Stack>
        <InputControl
          name="abn_number"
          label="Tax Payer Identification Number/เลขประจำตัวผู้เสียภาษีอากร"
          required
          value={state.formData.abn_number}
          onChange={onChange}
          error={!!validations.abn_number}
          helperText={validations.abn_number}
        />
        <Stack direction={{ xs: "column", sm: "row" }} gap="16px">
          <Box sx={{ width: { xs: "100%", sm: "160px" }, flexShrink: 0 }}>
            <CountrySelect
              name="business_country_code"
              label="Country Code/รหัสประเทศ"
              value={state.formData.business_country_code}
              onChange={(newValue) =>
                onChange({
                  target: {
                    name: "business_country_code",
                    value: newValue?.value,
                  },
                })
              }
            />
          </Box>
          <InputControl
            name="business_phone"
            label="Telephone Number/เบอร์โทรศัพท์"
            value={state.formData.business_phone}
            onChange={onChange}
            error={!!validations.business_phone}
            helperText={validations.business_phone}
          />
        </Stack>
        {!isGuest && (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>
              Save business details to customer profile/บันทึกข้อมูลธุรกิจในโปรไฟล์ลูกค้า
            </Typography>
            <Switch
              name="save_to_customer_profile"
              checked={state.formData.save_to_customer_profile}
              onChange={onChange}
            />
          </Stack>
        )}
        {remarks && (
          <Stack
            direction="row"
            alignItems="center"
            gap="8px"
            sx={{ p: "12px 16px", borderRadius: "8px", bgcolor: "#FFF7E0" }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
            <Typography variant="body2">Remarks: {remarks}</Typography>
          </Stack>
        )}
        <LoadingButton
          variant="contained"
          sx={{ alignSelf: "flex-end", mt: "8px" }}
          onClick={onSubmit}
          loading={state.isSubmitting}
        >
          PROCEED/ดำเนินการต่อ
        </LoadingButton>
      </Stack>
    </Stack>
  );
};

// Business details form used to create an FTI and to cancel & re-issue one.
// `remarks` is an optional note shown above the submit button.
export const FtiFormDialog = ({
  open,
  title,
  customer = null,
  initialValues,
  remarks = null,
  submitApi,
  onSuccess,
  onClose,
}) => {
  return (
    <CustomeDialog
      open={open}
      handleClose={onClose}
      disableCloseOnBackgroundClick
      title={title}
      customTitlePadding="4px 48px 16px 0"
      PaperProps={{
        sx: { width: "600px", maxWidth: "calc(100% - 32px)", p: "16px" },
      }}
      content={
        <FtiForm
          customer={customer}
          initialValues={initialValues}
          remarks={remarks}
          submitApi={submitApi}
          onSuccess={onSuccess}
        />
      }
    />
  );
};
